import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import { Sparkles, Send, MessageSquare, Mic, MicOff, Square, X, Wand2 } from "lucide-react"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import { Button } from "@/components/ui/button"
import { describeMicrophoneError, requestMicrophone } from "@/lib/microphone"
import { Textarea } from "@/components/ui/textarea"
import { useIntakeGuideStream } from "@/hooks/use-intake-guide-stream"
import { useMicPermission } from "@/hooks/use-mic-permission"
import { useTranscription } from "@/hooks/use-transcription"

// Section-specific copy for the kickoff experience. Each section gets:
//   opener: the first "AI" message that sets context
//   starters: 3 clickable suggestion chips that auto-send on click
//   summarizeLabel: the label on the "turn into bullets" CTA
const SECTION_CONFIG = {
  personal_goals: {
    label: "your top goals",
    opener:
      "Let's find 3–5 goals that would make this year matter. Pick a starting point below, or just start talking — I'll ask follow-up questions until the goals are specific.",
    starters: [
      "I want to grow spiritually this year",
      "I want my work and family life to feel more integrated",
      "Help me think through what a great year looks like",
    ],
    summarizeLabel: "Turn this into goals",
  },
  one_year_plan: {
    label: "your one-year plan",
    opener:
      "Tell me where the business is headed this year. Revenue, team, the biggest issue in your way — whatever's top of mind.",
    starters: [
      "Here's my revenue target — help me pressure-test it",
      "My biggest bottleneck right now is…",
      "I'm not sure what target to aim at",
    ],
    summarizeLabel: "Turn this into a plan",
  },
  kpis: {
    label: "your annual KPIs",
    opener:
      "KPIs are the numbers a healthy business watches weekly. Let's land on 3–5 that actually predict the outcome.",
    starters: [
      "I track revenue but not much else",
      "What KPIs matter for a coaching business?",
      "Help me pick leading indicators, not just lagging ones",
    ],
    summarizeLabel: "Turn this into KPIs",
  },
  kois: {
    label: "your annual KOIs",
    opener:
      "KOIs are spiritual practices that show you're walking with God — not generic \"pray more\" goals. What rhythm is God calling you to this year?",
    starters: [
      "I want to be more consistent in scripture",
      "I want a rhythm of sabbath",
      "Help me think about spiritual practices I could commit to",
    ],
    summarizeLabel: "Turn this into KOIs",
  },
  ministry: {
    label: "ministry in your business",
    opener:
      "How is your business already serving God and people beyond profit — and what small shift could make that native, not a side project?",
    starters: [
      "My business already serves people this way…",
      "I want ministry to feel native, not bolted on",
      "Help me think about what ministry could look like",
    ],
    summarizeLabel: "Turn this into ministry goals",
  },
  quarter_rocks: {
    label: "your quarter rocks",
    opener:
      "Rocks are the 3–5 things that, if they got done, would make this a winning quarter. What's jumping out?",
    starters: [
      "Here's what I know I need to do this quarter",
      "I'm torn between too many priorities",
      "Help me think about what matters most",
    ],
    summarizeLabel: "Turn this into rocks",
  },
}

function extractGuideOutput(text) {
  const match = text.match(/<guide-output>([\s\S]*?)<\/guide-output>/i)
  if (!match) return null
  return match[1]
    .split("\n")
    .map((l) => l.replace(/^[-*]\s*/, "").trim())
    .filter((l) => l.length > 0)
}

function formatTime(ms) {
  const safe = Number.isFinite(ms) && ms >= 0 ? ms : 0
  const secs = Math.floor(safe / 1000)
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${String(m).padStart(1, "0")}:${String(s).padStart(2, "0")}`
}

const MIC_MAX_SECONDS = 120
const MIC_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
]
function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return ""
  for (const t of MIC_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return t
    } catch {
      // ignore
    }
  }
  return ""
}

// variant:
//   "link" (default) — small inline link "Guide me through this"
//   "cta"  — primary button "Talk it through with AI"
export function GuidedSectionChat({ section, onInsert, variant = "link" }) {
  const config = SECTION_CONFIG[section] || {
    label: "this",
    opener: "Tell me what's on your mind.",
    starters: [],
    summarizeLabel: "Turn this into bullets",
  }

  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState([])
  const [draft, setDraft] = useState("")
  const scrollRef = useRef(null)
  const { isStreaming, currentChunk, error, sendMessage, abort } =
    useIntakeGuideStream()

  // In-dialog microphone. Mirrors VoiceField's flow: record → transcribe →
  // drop text into the draft (user can edit and hit Send). Skipping the
  // review panel here since the draft box itself is the review UI.
  const permission = useMicPermission()
  const transcription = useTranscription()
  const [micStage, setMicStage] = useState("idle") // 'idle' | 'recording' | 'uploading'
  const [micElapsedMs, setMicElapsedMs] = useState(0)
  const [micError, setMicError] = useState(null)
  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const mimeTypeRef = useRef("")
  const micStartedAtRef = useRef(0)
  const micTickerRef = useRef(null)
  const micAutoStopRef = useRef(null)
  const micCancelledRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      teardownMic()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    })
  }, [history, currentChunk])

  const teardownMic = () => {
    if (micTickerRef.current) {
      clearInterval(micTickerRef.current)
      micTickerRef.current = null
    }
    if (micAutoStopRef.current) {
      clearTimeout(micAutoStopRef.current)
      micAutoStopRef.current = null
    }
    const stream = streamRef.current
    streamRef.current = null
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop())
      } catch {
        // ignore
      }
    }
    if (recorderRef.current) {
      try {
        if (recorderRef.current.state !== "inactive") recorderRef.current.stop()
      } catch {
        // ignore
      }
      recorderRef.current = null
    }
    chunksRef.current = []
  }

  const handleOpenChange = (next) => {
    // Resolve the functional-updater form FIRST so we know whether the
    // dialog is closing before we decide to tear things down.
    const willBeOpen = typeof next === "function" ? next(open) : next
    setOpen(willBeOpen)
    if (!willBeOpen) {
      // Reset chat state on close so reopening starts fresh — prevents
      // an in-flight stream from committing an assistant message into
      // the NEXT session's history, and stops stale starter chips from
      // hiding behind old turns.
      abort()
      teardownMic()
      setMicStage("idle")
      setMicElapsedMs(0)
      setHistory([])
      setDraft("")
      setMicError(null)
      transcription.reset()
    }
  }

  const send = async (overrideContent) => {
    const content = (overrideContent ?? draft).trim()
    if (!content || isStreaming) return

    const newHistory = [...history, { role: "user", content }]
    setHistory(newHistory)
    setDraft("")

    await sendMessage({
      section,
      chatHistory: newHistory,
      onDone: (assembled) => {
        setHistory((h) => [...h, { role: "assistant", content: assembled }])
      },
    })
  }

  const sendStarter = (starter) => send(starter)

  const summarizeNow = () => {
    if (isStreaming) return
    // Only valuable once the user has said something substantive.
    if (history.length === 0 && !draft.trim()) return
    send("I'm ready — please summarize what I said into a concise bullet list wrapped in <guide-output> tags.")
  }

  const useGuideOutput = (assistantMessage) => {
    const bullets = extractGuideOutput(assistantMessage)
    if (!bullets || bullets.length === 0) return
    onInsert(bullets)
    setOpen(false)
  }

  const startMic = async () => {
    setMicError(null)
    transcription.reset()
    micCancelledRef.current = false
    chunksRef.current = []

    let stream
    try {
      // Shared with useAudioRecorder and VoiceField (see @/lib/microphone).
      // This used to be a bare getUserMedia: an unanswered permission prompt
      // never settles, so the mic hung with no error, and the local error
      // vocabulary was thinner than everywhere else — a missing or busy
      // microphone fell through to the raw browser message.
      stream = await requestMicrophone()
    } catch (err) {
      setMicError(describeMicrophoneError(err, "Could not access your microphone."))
      return
    }

    if (!isMountedRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    const mime = pickMimeType()
    mimeTypeRef.current = mime || "audio/webm"

    let recorder
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop())
      setMicError(err?.message || "Couldn't start the recorder.")
      return
    }

    streamRef.current = stream
    recorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onerror = (e) => {
      teardownMic()
      setMicError(e?.error?.message || "Recording failed.")
      setMicStage("idle")
    }
    recorder.onstop = async () => {
      const chunks = chunksRef.current
      const mimeType = mimeTypeRef.current || "audio/webm"
      chunksRef.current = []
      recorderRef.current = null
      teardownMic()

      if (!isMountedRef.current) return
      if (micCancelledRef.current) {
        micCancelledRef.current = false
        setMicStage("idle")
        setMicElapsedMs(0)
        return
      }

      const blob = new Blob(chunks, { type: mimeType })
      if (blob.size < 512) {
        setMicError("That recording was too short. Please try again.")
        setMicStage("idle")
        return
      }

      setMicStage("uploading")
      const text = await transcription.transcribe({
        blob,
        mimeType: mimeType,
        durationMs: micElapsedMs,
      })
      if (!isMountedRef.current) return
      if (text) {
        // Append to the draft so the user can edit before sending.
        setDraft((d) => (d.trim() ? `${d.trim()} ${text}` : text))
      }
      setMicStage("idle")
      setMicElapsedMs(0)
    }

    micStartedAtRef.current = Date.now()
    setMicElapsedMs(0)
    setMicStage("recording")
    recorder.start()

    micTickerRef.current = setInterval(() => {
      if (!isMountedRef.current) return
      setMicElapsedMs(Date.now() - micStartedAtRef.current)
    }, 250)

    micAutoStopRef.current = setTimeout(() => {
      try {
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop()
        }
      } catch {
        // ignore
      }
    }, MIC_MAX_SECONDS * 1000)
  }

  const stopMic = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop()
      }
    } catch {
      // ignore
    }
  }

  const cancelMic = () => {
    micCancelledRef.current = true
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop()
      } else {
        setMicStage("idle")
        setMicElapsedMs(0)
      }
    } catch {
      setMicStage("idle")
      setMicElapsedMs(0)
    }
    transcription.reset()
  }

  const micDisabled = permission === "denied" || permission === "unsupported"
  const readyToSummarize = history.length >= 2 && !isStreaming

  return (
    <>
      {variant === "cta" ? (
        <Button
          type="button"
          variant="gradient"
          size="default"
          onClick={() => setOpen(true)}
          className="shrink-0 whitespace-nowrap"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Talk it through
        </Button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs text-kf-red hover:underline"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Guide me through this
        </button>
      )}

      <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
        <ResponsiveDialogContent className="sm:max-w-lg">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              Talk through {config.label}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Think out loud — I'll ask probing questions. When you're ready,
              hit &ldquo;{config.summarizeLabel}&rdquo; and I'll turn it into
              clean bullets you can drop into the form.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div
            ref={scrollRef}
            className="h-72 overflow-y-auto px-4 py-3 bg-zinc-50 rounded-md space-y-3 border border-zinc-200"
          >
            {/* Always show the opener as a pseudo-first AI message so the
                dialog never looks like an empty blank prompt. */}
            <Message msg={{ role: "assistant", content: config.opener }} />

            {history.length === 0 && !currentChunk && config.starters.length > 0 && (
              <div className="flex flex-col gap-2 pt-1">
                <p className="text-xs uppercase tracking-wide text-zinc-500">
                  Quick start
                </p>
                {config.starters.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendStarter(s)}
                    className="text-left text-sm px-3 py-2 rounded-md bg-white border border-zinc-200 hover:border-kf-red hover:bg-kf-red/5 text-zinc-800 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {history.map((msg, idx) => (
              <Message key={idx} msg={msg} onUse={useGuideOutput} />
            ))}
            {isStreaming && currentChunk && (
              <Message
                msg={{ role: "assistant", content: currentChunk }}
                streaming
              />
            )}
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
          </div>

          <div className="px-4 pb-4 space-y-2">
            {/* Summarize CTA — hidden until there's actually something to summarize. */}
            {readyToSummarize && (
              <Button
                type="button"
                variant="kf-outline"
                className="w-full"
                onClick={summarizeNow}
              >
                <Wand2 className="w-4 h-4 mr-2" />
                {config.summarizeLabel}
              </Button>
            )}

            {/* Inline mic indicator when recording or uploading. */}
            {micStage === "recording" && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-3 rounded-md bg-kf-red/10 border border-kf-red/30 px-3 py-2"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-kf-red animate-pulse" />
                <span className="text-sm font-mono text-zinc-700">
                  {formatTime(micElapsedMs)}
                </span>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={cancelMic}
                    aria-label="Cancel recording"
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-zinc-600 hover:bg-white"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={stopMic}
                    aria-label="Stop and transcribe"
                    className="flex items-center gap-1 px-2 py-1 rounded bg-kf-red text-white text-xs"
                  >
                    <Square className="w-3 h-3" />
                    Stop
                  </button>
                </div>
              </div>
            )}
            {micStage === "uploading" && (
              <p role="status" aria-live="polite" className="text-sm text-zinc-500">
                Transcribing…
              </p>
            )}
            {micError && micStage === "idle" && (
              <p role="alert" className="text-sm text-red-600">
                {micError}
              </p>
            )}

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={micStage === "recording" ? "Recording…" : "Type or tap the mic to speak…"}
                  rows={2}
                  className="flex-1 resize-none pr-10"
                  disabled={micStage === "recording" || micStage === "uploading"}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      send()
                    }
                  }}
                />
                {!micDisabled && micStage === "idle" && (
                  <button
                    type="button"
                    onClick={startMic}
                    aria-label="Record voice input"
                    title="Tap to speak"
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-kf-red hover:border-kf-red transition-colors"
                  >
                    <Mic className="w-3.5 h-3.5" />
                  </button>
                )}
                {micDisabled && (
                  <div className="absolute top-2 right-2 p-1.5 text-zinc-300">
                    <MicOff className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="gradient"
                onClick={() => send()}
                disabled={!draft.trim() || isStreaming || micStage === "recording" || micStage === "uploading"}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  )
}

// Constrained markdown renderer for chat bubbles. The AI sends real
// markdown (headers, bold, lists, etc.) so plain-text rendering looks
// awful; react-markdown with tight tailwind overrides gives us a tidy,
// compact bubble that still reads well. No links/images rendered —
// the coaching output is conversational text only.
function MarkdownBubble({ content, isUser }) {
  const toneClass = isUser ? "text-white" : "text-zinc-800"
  return (
    <ReactMarkdown
      // Force block elements to render inline-friendly spacing inside
      // the bubble. prose-sm tailwind would work too, but it'd require
      // @tailwindcss/typography; these overrides are cheaper.
      components={{
        p: ({ node, ...props }) => (
          <p className={`mb-2 last:mb-0 ${toneClass}`} {...props} />
        ),
        ul: ({ node, ...props }) => (
          <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-1" {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-1" {...props} />
        ),
        li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
        strong: ({ node, ...props }) => (
          <strong className="font-semibold" {...props} />
        ),
        em: ({ node, ...props }) => <em className="italic" {...props} />,
        h1: ({ node, ...props }) => (
          <h3 className="font-semibold text-base mb-1" {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h3 className="font-semibold text-base mb-1" {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h3 className="font-semibold text-sm mb-1" {...props} />
        ),
        code: ({ node, inline, ...props }) =>
          inline ? (
            <code
              className={`rounded px-1 py-0.5 text-[0.85em] ${
                isUser ? "bg-white/20" : "bg-zinc-100"
              }`}
              {...props}
            />
          ) : (
            <pre className="bg-zinc-900 text-zinc-100 rounded p-2 text-xs overflow-x-auto my-2">
              <code {...props} />
            </pre>
          ),
        a: ({ node, ...props }) => (
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            className="underline"
            target="_blank"
            rel="noreferrer noopener"
            {...props}
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function Message({ msg, streaming, onUse }) {
  const isUser = msg.role === "user"
  const bullets = !isUser && onUse ? extractGuideOutput(msg.content) : null
  const displayContent = msg.content
    .replace(/<guide-output>[\s\S]*?<\/guide-output>/gi, "")
    .trim()

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? "bg-kf-red text-white"
            : "bg-white border border-zinc-200 text-zinc-800"
        }`}
      >
        {displayContent ? (
          <MarkdownBubble content={displayContent} isUser={isUser} />
        ) : streaming ? (
          "…"
        ) : (
          ""
        )}
        {bullets && bullets.length > 0 && (
          <div className="mt-3 border-t border-zinc-200 pt-2">
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
              Summary
            </p>
            <ul className="text-xs space-y-1 mb-2 list-disc pl-4">
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
            <Button
              type="button"
              size="sm"
              variant="gradient"
              onClick={() => onUse(msg.content)}
            >
              Use this
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
